/**
 * SDK Overview Page — /sdk
 *
 * Inspired by react.dev/reference: shows all categories with
 * brief descriptions and links to individual reference pages.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Package, Code2, Layers, FileText, Lightbulb } from 'lucide-react';
import { SDK_CATEGORIES, SDK_ENTRIES } from '../../data/sdk-docs';

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  'Getting Started': <Package size={20} />,
  'TokenKit Class': <Code2 size={20} />,
  'Helper Methods': <Layers size={20} />,
  'Types': <FileText size={20} />,
  'Guides': <Lightbulb size={20} />,
};

export default function SdkOverviewPage() {
  return (
    <div className="sdk-overview">
      {/* Hero */}
      <div className="sdk-overview-hero">
        <div className="sdk-overview-hero-icon">
          <BookOpen size={40} />
        </div>
        <h1>token-kit SDK Reference</h1>
        <p className="sdk-overview-hero-desc">
          The official TypeScript SDK for integrating LLM features into your
          applications using the token-kit end-user-funded token model.
        </p>
        <div className="sdk-overview-hero-actions">
          <Link to="/sdk/installation" className="btn btn-accent">
            Get Started
          </Link>
          <a
            href="https://github.com/token-kit/sdk"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline"
          >
            GitHub
          </a>
        </div>
      </div>

      {/* Quick install */}
      <div className="sdk-install-banner">
        <code>npm install @codetunezstudios/token-kit</code>
      </div>

      {/* Categories */}
      {SDK_CATEGORIES.map((cat) => {
        const entries = cat.items
          .map((slug) => SDK_ENTRIES.find((e) => e.slug === slug))
          .filter(Boolean) as typeof SDK_ENTRIES;

        return (
          <section key={cat.slug} className="sdk-category-section">
            <div className="sdk-category-header">
              <span className="sdk-category-icon">
                {CATEGORY_ICONS[cat.name] ?? <Code2 size={20} />}
              </span>
              <div>
                <h2>{cat.name}</h2>
                <p>{cat.description}</p>
              </div>
            </div>

            <div className="sdk-category-grid">
              {entries.map((entry) => (
                <Link
                  key={entry.slug}
                  to={`/sdk/${entry.slug}`}
                  className="sdk-entry-card"
                >
                  <div className="sdk-entry-card-header">
                    <span className={`sdk-kind-badge ${entry.kind}`}>
                      {kindLabel(entry.kind)}
                    </span>
                    {entry.since && (
                      <span className="sdk-since">v{entry.since}</span>
                    )}
                  </div>
                  <h3>{entry.title}</h3>
                  <p>{entry.summary}</p>
                </Link>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function kindLabel(kind: string): string {
  switch (kind) {
    case 'class':
      return 'Class';
    case 'method':
      return 'Method';
    case 'static-method':
      return 'Static';
    case 'type':
      return 'Type';
    case 'guide':
      return 'Guide';
    default:
      return kind;
  }
}
